import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Search, Phone, MessageCircle, Mail, Globe } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { fileUrl } from "@/lib/api";
import { getVisibleItems, itemHref, CONTACT_ICONS } from "@/components/ContactItems";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/new-machines", label: "New Machines" },
  { to: "/used-machines", label: "Used Machines" },
  { to: "/spare-parts", label: "Spare Parts" },
  { to: "/categories", label: "Categories" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/used-machines?search=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  const homeItems = getVisibleItems(settings, "home");
  const headerItems = homeItems.filter((i) => ["phone1", "phone2", "whatsapp", "email"].includes(i.key));
  const socialItems = homeItems.filter((i) => i.kind === "social");
  const iconFor = (k) => (k === "whatsapp" ? MessageCircle : k === "email" ? Mail : Phone);
  const shortLabel = (k) => (k === "whatsapp" ? "WhatsApp" : k === "email" ? "Email" : "Call");

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-md">
      {(headerItems.length > 0 || socialItems.length > 0) && (
        <div data-testid="header-contact-bar" className="bg-[hsl(var(--charcoal))]">
          <div className="mx-auto flex h-9 max-w-7xl items-center justify-end gap-3 px-4 sm:gap-4 sm:px-6 lg:px-8">
            {headerItems.map((item) => {
              const Icon = iconFor(item.key);
              return (
                <a key={item.id} href={itemHref(item)} data-testid={`header-${item.key}`}
                  {...(item.key === "whatsapp" ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="flex items-center gap-1.5 text-xs font-semibold text-slate-200 transition-colors hover:text-orange">
                  <Icon size={13} className="text-orange" />
                  <span className="hidden md:inline">{item.value}</span>
                  <span className="md:hidden">{shortLabel(item.key)}</span>
                </a>
              );
            })}
            {socialItems.length > 0 && (
              <div data-testid="header-socials" className="flex items-center gap-1 border-l border-white/15 pl-3">
                {socialItems.map((item) => {
                  const Icon = CONTACT_ICONS[item.icon] || Globe;
                  return (
                    <a key={item.id} href={itemHref(item)} target="_blank" rel="noopener noreferrer" title={item.label}
                      data-testid={`header-social-${item.key}`}
                      className="flex h-6 w-6 items-center justify-center rounded-full text-slate-300 transition-colors hover:bg-white/10 hover:text-orange">
                      <Icon size={13} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="brand-logo" className="flex flex-col leading-none">
          {settings.logo_url ? (
            <img src={fileUrl(settings.logo_url)} alt={settings.website_name || "Mashiniya"} className="h-9 w-auto object-contain" />
          ) : (
            <>
              <span className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
                MASHIN<span className="text-orange">IYA</span>
              </span>
              <span className="text-[10px] font-medium uppercase tracking-widest text-slate-400">
                by {settings.parent_company}
              </span>
            </>
          )}
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors hover:text-[hsl(var(--brand))] ${
                location.pathname === n.to ? "text-[hsl(var(--brand))]" : "text-slate-700"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/sell-your-machine"
            data-testid="sell-machine-cta"
            className="hidden rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors sm:inline-flex"
          >
            LIST YOUR PRODUCT
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="rounded-md p-2 text-slate-700 lg:hidden"
            onClick={() => setOpen(!open)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-4 lg:hidden">
          <form onSubmit={submitSearch} className="mb-3 flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2">
            <Search size={18} className="text-slate-400" />
            <input
              data-testid="mobile-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search machine, brand or model..."
              className="w-full bg-transparent text-sm outline-none"
            />
          </form>
          <div className="flex flex-col gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm font-semibold text-slate-700"
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/sell-your-machine"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-full bg-brand px-5 py-3 text-center text-sm font-bold text-white"
            >
              LIST YOUR PRODUCT
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
